import type { Wallet } from '@/api/auth';
import type { Currency } from '@/api/common';
import { amountWithCurrencySymbol } from './currency';

export const getCurrencyCentsAndCode = (
  amount: number,
  currency: string,
  currencies: Currency[],
  withoutSubunit?: boolean
): { currency: string; amount: number } => {
  const currentCurrency = currencies.find(({ code }) => code === currency);

  if (!currentCurrency) return { amount, currency };

  const { subunitsToUnit, subcurrencies } = currentCurrency;

  if (!withoutSubunit && amount < subunitsToUnit && subcurrencies.length !== 0)
    return {
      amount: amount / subcurrencies[0].subunitsToUnit,
      currency: subcurrencies[0].code,
    };

  return { amount: amount / subunitsToUnit, currency };
};

export const formatWalletValue = (
  currencyCode: string,
  amountCents: number,
  currencies: Currency[],
  withSymbol?: boolean,
  withoutSubunit?: boolean
) => {
  const { amount, currency } = getCurrencyCentsAndCode(
    amountCents,
    currencyCode,
    currencies,
    withoutSubunit
  );

  if (!withSymbol) return amount;

  return amountWithCurrencySymbol(currency, amount);
};

export const convertCurrencyIntoCents = (
  amount: number,
  currency: string,
  currencies: Currency[]
): { currency: string; amount: number } => {
  const currentCurrency = currencies.find(({ code }) => code === currency);

  if (!currentCurrency) return { amount, currency };

  const { subunitsToUnit, subcurrencies } = currentCurrency;

  if (amount <= subunitsToUnit && subcurrencies.length !== 0)
    return {
      amount: amount * subcurrencies[0].subunitsToUnit,
      currency: subcurrencies[0].code,
    };

  return { amount: amount * subunitsToUnit, currency };
};

export const mergeAccountByCurrency = <T extends { currency: string }>(
  accounts: T[],
  incoming: T
): T[] => {
  const index = accounts.findIndex(
    ({ currency }) => currency === incoming.currency
  );

  if (index === -1) return [...accounts, incoming];

  const merged = [...accounts];
  merged[index] = { ...merged[index], ...incoming };

  return merged;
};

export const mergeBalanceIntoWallets = (
  wallets: Wallet[],
  balance: Wallet
): Wallet[] => {
  const index = wallets.findIndex(
    ({ currency }) => currency === balance.currency
  );

  if (index === -1) return wallets;

  const merged = [...wallets];
  merged[index] = {
    ...merged[index],
    amountCents: balance.amountCents,
    availableToCashoutCents: balance.availableToCashoutCents,
  };

  return merged;
};
